import { IntlShape } from 'react-intl';
import { STUDIO_THEMES } from 'fym-shared/src/music24/studioThemes';
import type { SerializedMediaWidgetData } from 'fym-shared/src/smartlinks/mediaWidget';
import { kitVariantLabel } from '../../../../music24/promote/kitLabels';
import { studioThemeMessages } from '../../../../music24/promote/studio/studioThemeMessages';
import { mediaBlockMessages } from './mediaBlockMessages';

/**
 * The name of the file of a Media block: "Story (Out now)", "AI reel 15s", "Cover", or "Picture" / "Video" for an upload.
 * It follows the rule of the Composer picker cards (`revisionCardLabel`), so the same file has the
 * same name on both screens.
 */
export function mediaBlockLabel(intl: IntlShape, data: Pick<SerializedMediaWidgetData, 'source' | 'media'>): string {
  const { source } = data;
  if (source.kind === 'upload') {
    // The kind of the file, not its raw file name (QA F23): "swamps-single-artwork.jpg" says nothing to an artist.
    const kind = data.media?.kind;
    if (kind === 'image') {
      return intl.formatMessage(mediaBlockMessages.uploadedPicture);
    }
    return intl.formatMessage(kind === 'video' ? mediaBlockMessages.uploadedVideo : mediaBlockMessages.uploadedFile);
  }
  if (source.kind === 'reel') {
    return `${intl.formatMessage(studioThemeMessages.aiReel.name)} ${source.reelVariant}`;
  }
  if (source.kind === 'cover') {
    return intl.formatMessage(mediaBlockMessages.cover);
  }
  const theme = STUDIO_THEMES[source.themeId];
  const name = intl.formatMessage(studioThemeMessages[source.themeId].name);
  const namesVariant = source.themeId === 'story' || source.themeId === 'feedPost' || theme.outputs.length > 1;
  if (!namesVariant) {
    return name;
  }
  const specId = theme.outputs.find((output) => output.variant === source.variant)?.specId ?? theme.outputs[0]?.specId;
  if (specId === undefined) {
    return name;
  }
  return `${name} (${kitVariantLabel(intl, { variant: source.variant, specId })})`;
}

/** The host of an address for "opens mikasol.shop"; null when the address does not parse. */
export function linkHost(url: string): string | null {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** The main option of a block: "opens mikasol.shop", "autoplay muted" or "tap to play". Null for a plain image. */
export function mediaBlockMainOption(intl: IntlShape, data: SerializedMediaWidgetData): string | null {
  if (data.linkUrl !== null) {
    const host = linkHost(data.linkUrl);
    if (host !== null) {
      return intl.formatMessage(mediaBlockMessages.optionOpens, { host });
    }
  }
  if (data.media?.kind !== 'video') {
    return null;
  }
  return intl.formatMessage(data.autoplay ? mediaBlockMessages.optionAutoplay : mediaBlockMessages.optionTapToPlay);
}

/**
 * The grey line of a block row (design a3): the file name, the revision and the main option, or the
 * reason why the page does not show the block (design d1).
 */
export function mediaBlockDetail(intl: IntlShape, data: SerializedMediaWidgetData): string {
  if (data.media === null) {
    switch (data.problem) {
      case 'songGone':
        return intl.formatMessage(mediaBlockMessages.hiddenSongGone);
      case 'rendering':
        return intl.formatMessage(mediaBlockMessages.hiddenRendering);
      case 'fileGone':
      case null:
        return intl.formatMessage(mediaBlockMessages.hiddenFileGone);
    }
  }
  const parts = [
    // The grey line keeps the file name of an upload, so two uploads stay apart.
    data.source.kind === 'upload' ? (data.media.fileName ?? mediaBlockLabel(intl, data)) : mediaBlockLabel(intl, data),
    data.media.revision === null ? null : `r${data.media.revision}`,
    mediaBlockMainOption(intl, data),
  ];
  return parts.filter((part): part is string => part !== null).join(' · ');
}

/** True when the page does not show the block now. */
export function isMediaBlockHidden(data: SerializedMediaWidgetData): boolean {
  return data.media === null;
}
